import React, { useContext, useEffect, useState } from "react";
import DataTable from "react-data-table-component";
import { HiOutlinePencilSquare } from "react-icons/hi2";
import { IoTrashOutline } from "react-icons/io5";
import { FiEye, FiEyeOff, FiUnlock } from "react-icons/fi";
import { AppContext } from "../../Context/Provider";
import {
  getPasswordRuleStates,
  validatePassword,
} from "../../Utilities/validatePassword";

const initialFormData = {
  username: "",
  name: "",
  email: "",
  phone: "",
  password: "",
  confirmPassword: "",
};

function UsersManagement() {
  const {
    FetchUsers,
    UsersData,
    CreateUser,
    UpdateUser,
    DeleteUser,
    UnlockUser,
  } = useContext(AppContext);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [filterText, setFilterText] = useState("");
  const [currentId, setCurrentId] = useState("");
  const [deleteId, setDeleteId] = useState("");
  const [formData, setFormData] = useState(initialFormData);
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  useEffect(() => {
    FetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resetForm = () => {
    setFormData(initialFormData);
    setCurrentId("");
    setEditMode(false);
    setError("");
    setShowPassword(false);
    setShowConfirmPassword(false);
  };

  const openCreateModal = () => {
    resetForm();
    setShowModal(true);
  };

  const openEditModal = (row) => {
    setFormData({
      username: row.username || "",
      name: row.name || "",
      email: row.email || "",
      phone: row.phone?.toString?.() || "",
      password: "",
      confirmPassword: "",
    });
    setCurrentId(row._id);
    setEditMode(true);
    setError("");
    setShowModal(true);
  };

  const handleDelete = (id) => {
    setDeleteId(id);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    const deleted = await DeleteUser(deleteId);

    if (deleted) {
      setDeleteId("");
      setShowDeleteModal(false);
    }
  };

  const handleChange = ({ target: { name, value } }) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (error) {
      setError("");
    }
  };

  const validateForm = () => {
    const trimmedData = {
      username: formData.username.trim(),
      name: formData.name.trim(),
      email: formData.email.trim(),
      phone: formData.phone.trim(),
      password: formData.password,
      confirmPassword: formData.confirmPassword,
    };

    if (
      !trimmedData.username ||
      !trimmedData.name ||
      !trimmedData.email ||
      !trimmedData.phone
    ) {
      return "Please fill out all required fields.";
    }

    const passwordWasProvided = Boolean(trimmedData.password);

    if (!editMode && !passwordWasProvided) {
      return "Password is required when creating a user.";
    }

    if (passwordWasProvided) {
      const passwordError = validatePassword(
        trimmedData.password,
        trimmedData.username,
      );

      if (passwordError) {
        return passwordError;
      }

      if (trimmedData.password !== trimmedData.confirmPassword) {
        return "Passwords do not match.";
      }
    }

    return null;
  };

  const handleSubmit = async () => {
    const validationError = validateForm();

    if (validationError) {
      setError(validationError);
      return;
    }

    const payload = {
      username: formData.username.trim(),
      name: formData.name.trim(),
      email: formData.email.trim(),
      phone: formData.phone.trim(),
    };

    if (formData.password) {
      payload.password = formData.password;
      payload.confirmPassword = formData.confirmPassword;
    }

    setIsLoading(true);

    try {
      let saved = false;

      if (editMode) {
        saved = await UpdateUser(currentId, payload);
      } else {
        saved = await CreateUser(payload);
      }

      if (saved) {
        setShowModal(false);
        resetForm();
      }
    } finally {
      setIsLoading(false);
    }
  };

  const columns = [
    {
      name: "Username",
      selector: (row) => row.username,
      sortable: true,
    },
    {
      name: "Name",
      selector: (row) => row.name,
      sortable: true,
    },
    {
      name: "Email",
      selector: (row) => row.email,
      grow: 1.5,
    },
    {
      name: "Phone",
      selector: (row) => row.phone,
    },
    {
      name: "Login status",
      cell: (row) =>
        row.lockUntil && new Date(row.lockUntil).getTime() > Date.now() ? (
          <span className="font-medium text-red-600">Locked</span>
        ) : (
          <span className="text-green-600">Active</span>
        ),
    },
    {
      name: "Actions",
      cell: (row) => (
        <div className="flex space-x-2">
          <button
            onClick={() => openEditModal(row)}
            className="text-custom-green hover:text-blue-700"
          >
            <HiOutlinePencilSquare />
          </button>
          <button
            onClick={() => handleDelete(row._id)}
            className="text-custom-red hover:text-custom-yellow"
          >
            <IoTrashOutline />
          </button>
          {row.lockUntil && new Date(row.lockUntil).getTime() > Date.now() ? (
            <button
              type="button"
              onClick={() => UnlockUser(row._id)}
              className="text-blue-600 hover:text-blue-800"
              aria-label={`Unlock ${row.username}`}
              title="Unlock account"
            >
              <FiUnlock />
            </button>
          ) : null}
        </div>
      ),
      button: true,
      grow: 0.5,
    },
  ];

  const filteredItems = (UsersData || []).filter((item) => {
    const haystack = [
      item.username,
      item.name,
      item.email,
      item.phone?.toString?.(),
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();

    return haystack.includes(filterText.toLowerCase());
  });

  const subHeaderComponent = (
    <div className="flex flex-col items-end space-y-2 w-full p-4">
      <button
        onClick={openCreateModal}
        className="px-4 py-2 bg-custom-green text-white rounded"
      >
        + Add
      </button>
      <input
        type="text"
        onChange={(e) => setFilterText(e.target.value)}
        value={filterText}
        placeholder="Search here"
        className="border rounded p-2 w-full max-w-xs"
      />
    </div>
  );

  const customStyles = {
    rows: {
      style: {
        minHeight: "64px",
      },
    },
    headCells: {
      style: {
        paddingLeft: "8px",
        paddingRight: "8px",
        backgroundColor: "#f8f9fa",
        fontWeight: "bold",
      },
    },
    cells: {
      style: {
        paddingLeft: "8px",
        paddingRight: "8px",
      },
    },
  };

  const passwordRules = getPasswordRuleStates(
    formData.password,
    formData.username.trim(),
  );

  return (
    <div className="px-8 py-2">
      <DataTable
        columns={columns}
        data={filteredItems}
        pagination
        subHeader
        subHeaderComponent={subHeaderComponent}
        persistTableHead
        customStyles={customStyles}
        className="shadow-lg bg-white rounded"
      />

      {showModal && (
        <div className="fixed inset-0 bg-gray-800 bg-opacity-50 flex justify-center items-center z-50 px-4">
          <div className="bg-white p-6 rounded shadow-lg w-full max-w-2xl">
            <h2 className="text-xl font-bold mb-4">
              {editMode ? "Edit User" : "Create User"}
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-gray-700 mb-2">Username</label>
                <input
                  type="text"
                  name="username"
                  value={formData.username}
                  onChange={handleChange}
                  className="w-full p-2 border rounded"
                  placeholder="Enter username"
                />
              </div>
              <div>
                <label className="block text-gray-700 mb-2">Full Name</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className="w-full p-2 border rounded"
                  placeholder="Enter full name"
                />
              </div>
              <div>
                <label className="block text-gray-700 mb-2">Email</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full p-2 border rounded"
                  placeholder="Enter email"
                />
              </div>
              <div>
                <label className="block text-gray-700 mb-2">Phone</label>
                <input
                  type="text"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full p-2 border rounded"
                  placeholder="Enter phone"
                />
              </div>
              <div>
                <label className="block text-gray-700 mb-2">
                  {editMode ? "New Password" : "Password"}
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    className="w-full p-2 pr-10 border rounded"
                    placeholder={
                      editMode
                        ? "Leave blank to keep current password"
                        : "Enter password"
                    }
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((prev) => !prev)}
                    className="absolute inset-y-0 right-0 flex items-center px-3 text-gray-500"
                  >
                    {showPassword ? <FiEyeOff /> : <FiEye />}
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-gray-700 mb-2">
                  Confirm Password
                </label>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    className="w-full p-2 pr-10 border rounded"
                    placeholder="Confirm password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword((prev) => !prev)}
                    className="absolute inset-y-0 right-0 flex items-center px-3 text-gray-500"
                  >
                    {showConfirmPassword ? <FiEyeOff /> : <FiEye />}
                  </button>
                </div>
              </div>
            </div>

            <div className="mb-4 rounded bg-gray-50 p-4 text-sm text-gray-700">
              <p className="font-semibold mb-2">Password policy</p>
              <ul className="list-disc pl-5 space-y-1">
                {passwordRules.map((rule) => (
                  <li
                    key={rule.key}
                    className={rule.passed ? "text-green-600" : "text-red-600"}
                  >
                    {rule.label}
                  </li>
                ))}
              </ul>
            </div>

            {error && (
              <div className="mb-4 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                {error}
              </div>
            )}

            <div className="flex justify-end space-x-2">
              <button
                onClick={() => {
                  setShowModal(false);
                  resetForm();
                }}
                className="px-4 py-2 bg-gray-200 rounded"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={isLoading}
                className="px-4 py-2 bg-custom-green text-white rounded disabled:opacity-70"
              >
                {isLoading ? "Saving..." : editMode ? "Update" : "Create"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showDeleteModal && (
        <div className="fixed inset-0 bg-gray-800 bg-opacity-50 flex justify-center items-center z-50 px-4">
          <div className="bg-white p-6 rounded shadow-lg w-full max-w-md">
            <h2 className="text-lg font-semibold mb-3">Delete User</h2>
            <p className="text-gray-600 mb-4">
              Are you sure you want to delete this user?
            </p>
            <div className="flex justify-end space-x-2">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setDeleteId("");
                }}
                className="px-4 py-2 bg-gray-200 rounded"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDelete}
                className="px-4 py-2 bg-red-600 text-white rounded"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default UsersManagement;
